<template>
  <div class="ai-sidebar-container">
    <transition name="ai-sidebar-fade">
      <div v-if="isOpen" class="ai-sidebar-panel">
        <div class="ai-sidebar-header">
          <div class="header-main">
            <i class="fas fa-robot header-icon"></i>
            <div class="header-text">
              <div class="header-title">WHMAGs AI Assistant</div>
              <div class="header-subtitle">{{ contextLabel }}</div>
            </div>
          </div>
          <div class="header-actions">
            <el-tooltip placement="bottom" effect="dark">
              <div slot="content">Clear conversation</div>
              <i
                v-if="aiChatHistory.length && !aiLoading"
                class="el-icon-delete header-action-icon"
                @click="clearAIChat"
              ></i>
            </el-tooltip>
            <el-tooltip placement="bottom" effect="dark">
              <div slot="content">Hide assistant</div>
              <i class="el-icon-close header-action-icon" @click="toggleOpen"></i>
            </el-tooltip>
          </div>
        </div>
        <div class="ai-sidebar-body">
          <div v-if="suggestedQuestions.length" class="ai-sidebar-suggestions">
            <div class="suggestions-title">Suggested questions</div>
            <div class="suggestions-list">
              <span
                v-for="(item, index) in suggestedQuestions"
                :key="index"
                class="suggestion-chip"
                @click="useSuggestion(item)"
              >
                {{ item }}
              </span>
            </div>
          </div>
          <div class="ai-chat-history" ref="chatHistory">
            <div
              v-for="(msg, index) in aiChatHistory"
              :key="index"
              :class="['ai-message', msg.role]"
            >
              <div class="ai-message-role">
                {{ msg.role === 'user' ? 'You' : 'AI' }}
              </div>
              <div class="ai-message-content">
                {{ msg.content }}
              </div>
            </div>
            <div v-if="aiLoading" class="ai-message assistant ai-loading">
              <div class="ai-message-role">AI</div>
              <div class="ai-message-content">Thinking...</div>
            </div>
          </div>
        </div>
        <div class="ai-sidebar-input">
          <el-input
            type="textarea"
            v-model="aiQuestion"
            :rows="3"
            :placeholder="inputPlaceholder"
          ></el-input>
          <div class="ai-input-actions">
            <el-button @click="clearAIChat" :disabled="aiLoading || !aiChatHistory.length">
              Clear
            </el-button>
            <el-button type="primary" :loading="aiLoading" @click="sendAIQuestion">
              Send
            </el-button>
          </div>
        </div>
      </div>
    </transition>

    <div class="ai-sidebar-toggle" @click="toggleOpen">
      <div class="toggle-icon-wrapper">
        <i class="fas fa-robot"></i>
      </div>
      <div class="toggle-label">
        WHMAGs AI
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'GlobalAiSidebar',
  data() {
    return {
      isOpen: false,
      aiQuestion: '',
      aiChatHistory: [],
      aiLoading: false
    };
  },
  computed: {
    contextLabel() {
      const path = this.$route && this.$route.path ? this.$route.path : '';
      if (path.indexOf('/front/FuncQualityAssessment') === 0) {
        return 'You are in the Quality Assessment analysis module.';
      }
      if (path.indexOf('/front/FuncSpeciesAnnotation') === 0) {
        return 'You are in the Species Annotation module.';
      }
      if (path.indexOf('/front/FuncStructuralAnnotation') === 0) {
        return 'You are in the Structural Annotation module.';
      }
      if (path.indexOf('/front/FuncFunctionalAnnotation') === 0) {
        return 'You are in the Functional Annotation module.';
      }
      if (path.indexOf('/front/DataOverview') === 0) {
        return 'You are viewing the Data Overview module.';
      }
      if (path.indexOf('/front/DataSampleMetadata') === 0) {
        return 'You are viewing the Sample Metadata module.';
      }
      if (path.indexOf('/front/DataAssemblyQuality') === 0) {
        return 'You are viewing the Assembly Quality module.';
      }
      if (path.indexOf('/front/DataTaxonomicAnnotation') === 0) {
        return 'You are viewing the Taxonomic Annotation module.';
      }
      if (path.indexOf('/front/species-directory') === 0) {
        return 'You are viewing the Species Directory module.';
      }
      if (path.indexOf('/front/species/') === 0) {
        return 'You are viewing a Species Detail page.';
      }
      if (path.indexOf('/front') === 0) {
        return 'You are in the WHMAGs front-end interface.';
      }
      if (path === '/DataPlatform') {
        return 'You are in the DataPlatform data hub.';
      }
      return 'WHMAGs platform';
    },
    suggestedQuestions() {
      const path = this.$route && this.$route.path ? this.$route.path : '';
      if (path.indexOf('/front/FuncQualityAssessment') === 0) {
        return [
          'How should I interpret genome quality scores across samples?',
          'Which quality control metrics are most important in wildlife metagenome projects?'
        ];
      }
      if (path.indexOf('/front/FuncSpeciesAnnotation') === 0) {
        return [
          'How can I identify potential novel species from MAGs annotation results?',
          'For a specific MAG, which key indicators should I focus on?'
        ];
      }
      if (path.indexOf('/front/FuncStructuralAnnotation') === 0) {
        return [
          'Which structural features in the annotation are linked to host adaptation?',
          'How can I use structural annotation to discover important functional genes?'
        ];
      }
      if (path.indexOf('/front/FuncFunctionalAnnotation') === 0) {
        return [
          'How do KEGG or CAZy functional profiles relate to host diet?',
          'What functional enrichment patterns are common in wildlife gut metagenomes?'
        ];
      }
      if (path.indexOf('/front/DataOverview') === 0) {
        return [
          'How can I quickly compare microbiome composition across different host species?',
          'What comparative analyses can I design based on these overview plots?'
        ];
      }
      if (path.indexOf('/front/DataSampleMetadata') === 0) {
        return [
          'How can I use sample metadata to control for host-related confounders?',
          'Which environmental factors are most critical in wildlife metagenome studies?'
        ];
      }
      if (path.indexOf('/front/DataAssemblyQuality') === 0) {
        return [
          'How does assembly quality affect downstream MAG construction and annotation?',
          'When should I consider re-assembling data or adding more sequencing?'
        ];
      }
      if (path.indexOf('/front/DataTaxonomicAnnotation') === 0) {
        return [
          'How can I identify host-specific microbial taxa from taxonomic profiles?',
          'What should I pay attention to when comparing taxonomic composition across hosts?'
        ];
      }
      if (path.indexOf('/front/species-directory') === 0) {
        return [
          'How can I prioritize species for deeper microbiome analysis using this directory?',
          'Which species have larger sample sizes and are suitable for comparative studies?'
        ];
      }
      if (path.indexOf('/front/species/') === 0) {
        return [
          'For this species, how can I design host–microbiome association analyses?',
          'What is currently known about the gut microbiome of this species?'
        ];
      }
      if (path.indexOf('/front') === 0 || path === '/DataPlatform') {
        return [
          'How can I design a wildlife metagenomics study using WHMAGs data?',
          'What classic research questions exist for cetacean and ungulate gut microbiomes?'
        ];
      }
      return [];
    },
    inputPlaceholder() {
      return 'Ask any question about wildlife microbiome or metagenomics (English only).';
    }
  },
  methods: {
    toggleOpen() {
      this.isOpen = !this.isOpen;
      if (this.isOpen) {
        this.$nextTick(this.scrollChatToBottom);
      }
    },
    sendAIQuestion() {
      if (!this.aiQuestion || !this.aiQuestion.trim()) {
        if (this.$message) {
          this.$message.warning('Please enter a question');
        }
        return;
      }
      const question = this.aiQuestion.trim();
      this.aiChatHistory.push({ role: 'user', content: question });
      this.aiQuestion = '';
      this.aiLoading = true;
      if (!this.request) {
        this.aiChatHistory.push({
          role: 'assistant',
          content: 'Frontend HTTP client is not configured. Cannot call backend AI service.'
        });
        this.aiLoading = false;
        this.$nextTick(this.scrollChatToBottom);
        return;
      }
      this.request
        .post('/ai/chat', { question })
        .then(res => {
          if (!res || res.code !== '200') {
            if (this.$message) {
              this.$message.error((res && res.msg) || 'AI service request failed');
            }
            return;
          }
          let answer = '';
          if (res.data) {
            if (typeof res.data === 'string') {
              answer = res.data;
            } else if (res.data.answer) {
              answer = res.data.answer;
            }
          }
          if (!answer) {
            answer = 'No AI response received. Please try again later.';
          }
          this.aiChatHistory.push({ role: 'assistant', content: answer });
        })
        .catch(error => {
          let fallback = 'The intelligent Q&A service is currently slow or the network is unstable, so a full answer could not be retrieved.';
          const message = error && error.message ? String(error.message) : '';
          if (message && message.indexOf('timeout') !== -1) {
            fallback += ' (request timeout)';
          }
          fallback += '\n\nYou can try again later or first use other analysis tools on this platform.';
          this.aiChatHistory.push({
            role: 'assistant',
            content: fallback
          });
        })
        .finally(() => {
          this.aiLoading = false;
          this.$nextTick(this.scrollChatToBottom);
        });
    },
    clearAIChat() {
      this.aiChatHistory = [];
      this.aiQuestion = '';
    },
    scrollChatToBottom() {
      const el = this.$refs.chatHistory;
      if (el && el.scrollHeight !== undefined) {
        el.scrollTop = el.scrollHeight;
      }
    },
    useSuggestion(text) {
      if (!text) {
        return;
      }
      this.aiQuestion = text;
      this.$nextTick(() => {
        this.sendAIQuestion();
      });
    }
  }
};
</script>

<style scoped>
.ai-sidebar-container {
  position: fixed;
  right: 24px;
  top: 50%;
  transform: translateY(-50%);
  z-index: var(--z-modal);
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  pointer-events: none;
}

.ai-sidebar-panel {
  width: 360px;
  max-width: 92vw;
  max-height: 75vh;
  background: rgba(255, 255, 255, 0.98);
  border-radius: 16px;
  box-shadow: 0 18px 45px rgba(15, 23, 42, 0.24);
  border: 1px solid rgba(226, 232, 240, 0.9);
  display: flex;
  flex-direction: column;
  padding: 14px 14px 12px;
  margin-bottom: 12px;
  pointer-events: auto;
}

.ai-sidebar-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
}

.header-main {
  display: flex;
  align-items: center;
}

.header-icon {
  font-size: 1.4rem;
  color: #8e44ad;
  margin-right: 10px;
}

.header-text {
  display: flex;
  flex-direction: column;
}

.header-title {
  font-size: 1.1rem;
  font-weight: 700;
  color: #111827;
}

.header-subtitle {
  font-size: 0.82rem;
  color: #6b7280;
  margin-top: 2px;
}

.header-actions {
  display: flex;
  align-items: center;
  gap: 8px;
}

.header-action-icon {
  font-size: 1.1rem;
  color: #9ca3af;
  cursor: pointer;
  transition: color 0.2s ease, transform 0.2s ease;
}

.header-action-icon:hover {
  color: #4b5563;
  transform: translateY(-1px);
}

.ai-sidebar-body {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.ai-sidebar-suggestions {
  margin-bottom: 4px;
}

.suggestions-title {
  font-size: 0.85rem;
  font-weight: 600;
  color: #4b5563;
  margin-bottom: 4px;
}

.suggestions-list {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.suggestion-chip {
  display: inline-flex;
  align-items: center;
  padding: 4px 8px;
  font-size: 0.78rem;
  border-radius: 999px;
  border: 1px solid rgba(148, 163, 184, 0.6);
  color: #111827;
  background: rgba(248, 250, 252, 0.9);
  cursor: pointer;
  transition: all 0.2s ease;
}

.suggestion-chip:hover {
  background: linear-gradient(135deg, #eef2ff, #e0f2fe);
  border-color: #4f46e5;
  color: #111827;
  transform: translateY(-1px);
}

.ai-chat-history {
  flex: 1;
  min-height: 120px;
  max-height: 260px;
  overflow-y: auto;
  padding: 8px;
  background: #f9fafb;
  border-radius: 10px;
  border: 1px solid #e5e7eb;
}

.ai-message {
  margin-bottom: 6px;
  padding: 6px 8px;
  border-radius: 8px;
  background: #ffffff;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04);
}

.ai-message.user {
  border-left: 3px solid #2563eb;
}

.ai-message.assistant {
  border-left: 3px solid #16a34a;
}

.ai-message-role {
  font-size: 0.78rem;
  font-weight: 600;
  color: #6b7280;
  margin-bottom: 2px;
}

.ai-message-content {
  font-size: 0.9rem;
  color: #111827;
  white-space: pre-wrap;
}

.ai-sidebar-input {
  margin-top: 10px;
}

.ai-input-actions {
  display: flex;
  justify-content: flex-end;
  gap: 6px;
  margin-top: 6px;
}

.ai-sidebar-toggle {
  pointer-events: auto;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  cursor: pointer;
}

.toggle-icon-wrapper {
  width: 54px;
  height: 54px;
  border-radius: 999px;
  background: linear-gradient(135deg, #8e44ad, #2ecc71);
  box-shadow: 0 12px 30px rgba(76, 81, 191, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.toggle-icon-wrapper i {
  font-size: 1.5rem;
  color: #ffffff;
}

.toggle-label {
  font-size: 0.78rem;
  color: #374151;
  background: rgba(255, 255, 255, 0.92);
  padding: 3px 8px;
  border-radius: 999px;
  box-shadow: 0 4px 14px rgba(148, 163, 184, 0.35);
}

.ai-sidebar-toggle:hover .toggle-icon-wrapper {
  transform: translateY(-2px);
  box-shadow: 0 16px 40px rgba(76, 81, 191, 0.6);
}

.ai-sidebar-fade-enter-active,
.ai-sidebar-fade-leave-active {
  transition: all 0.2s ease;
}

.ai-sidebar-fade-enter,
.ai-sidebar-fade-leave-to {
  opacity: 0;
  transform: translateY(-52%);
}
</style>
